import { getAnalytics, logEvent as firebaseLogEvent } from "firebase/analytics"
import { getToken, getMessaging, onMessage } from "firebase/messaging"
import { initializeApp } from "firebase/app"

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

const WEB_PUSH_CERTIFICATE = import.meta.env
  .VITE_FIREBASE_WEB_PUSH_CERTIFICATE as string | undefined

const app = initializeApp(firebaseConfig)

let analytics: ReturnType<typeof getAnalytics> | null = null
if (typeof window !== "undefined") {
  try {
    analytics = getAnalytics(app)
  } catch {
    analytics = null
  }
}

let messaging: ReturnType<typeof getMessaging> | null = null

function getNotificationPermission(): NotificationPermission | "unsupported" {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported"
  }
  return Notification.permission
}

async function requestNotificationPermission(): Promise<
  NotificationPermission | "unsupported"
> {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported"
  }
  return Notification.requestPermission()
}

function getMessagingInstance() {
  if (typeof window === "undefined") return null
  if (!messaging) {
    try {
      messaging = getMessaging(app)
    } catch {
      return null
    }
  }
  return messaging
}

async function requestFCMToken(): Promise<string | null> {
  if (typeof window === "undefined" || !("Notification" in window)) return null
  const vapidKey = WEB_PUSH_CERTIFICATE
  if (!vapidKey) return null
  const permission = await requestNotificationPermission()
  if (permission !== "granted") return null
  const messagingInstance = getMessagingInstance()
  if (!messagingInstance) return null
  try {
    const token = await getToken(messagingInstance, { vapidKey })
    return token
  } catch {
    return null
  }
}

function subscribeToForegroundMessages(
  callback: (payload: import("firebase/messaging").MessagePayload) => void
): (() => void) | undefined {
  const messagingInstance = getMessagingInstance()
  if (!messagingInstance) return undefined
  return onMessage(messagingInstance, callback)
}

function logPageView(path: string, title?: string): void {
  if (!analytics) return
  try {
    firebaseLogEvent(analytics, "page_view", {
      page_path: path,
      page_title: title ?? document.title,
    })
  } catch {
    // no-op
  }
}

function logAnalyticsEvent(
  name: string,
  params?: Record<string, string | number | boolean>
): void {
  if (!analytics) return
  try {
    firebaseLogEvent(analytics, name, params)
  } catch {
    // no-op
  }
}

export {
  analytics,
  WEB_PUSH_CERTIFICATE,
  getNotificationPermission,
  requestNotificationPermission,
  requestFCMToken,
  subscribeToForegroundMessages,
  logPageView,
  logAnalyticsEvent,
}
